import { useEffect, useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { AllowedPaymentMethodConfigPayload } from '@/modules/employer/types';
import { PaymentMethodType } from '@/modules/ReimburseMe/types/payment.types';

const GET_ORGANIZATION_PROPERTIES_QUERY_KEY = 'getOrganizationPropertiesQueryKey';

export interface OrganizationProperties {
  isReimbursementAllowed: boolean;
  isBillPayAllowed: boolean;
  allowedPaymentMethods: PaymentMethodType[];
  customerServiceIsEnabled?: boolean;
  customerServiceNumber?: string;
  customerServiceUrlName?: string;
  customerServiceUrlLink?: string;
  customerServiceEmail?: string;
}

export interface OrganizationPropertiesDto {
  is_reimbursement_allowed: boolean;
  is_bill_pay_allowed: boolean;
  allowed_payment_method_configs: AllowedPaymentMethodConfigPayload[];
  customer_service_is_enabled?: boolean;
  customer_service_number?: string;
  customer_service_url_name?: string;
  customer_service_url_link?: string;
  customer_service_email?: string;
}

const useOrganizationPropertiesQuery = (organizationId?: string, enabled: boolean = true) => {
  const { isLoading, isSuccess, isError, data, refetch } = useQuery(
    [GET_ORGANIZATION_PROPERTIES_QUERY_KEY, organizationId, enabled],
    () =>
      api.get(API_URL.ORGANIZATIONS_OVERRIDABLE_PROPERTIES_EMPLOYEE_BY_ORGANIZATION_ID(organizationId || '')),
    {
      enabled: !!organizationId && enabled,
    }
  );

  useEffect(() => {
    if (organizationId) refetch();
  }, [organizationId, refetch]);

  const formattedData = useMemo(
    () =>
      data
        ? {
            isReimbursementAllowed: data?.data?.is_reimbursement_allowed,
            isBillPayAllowed: data?.data?.is_bill_pay_allowed || false,
            allowedPaymentMethods:
              // TODO: disappeared from DTO, potential to remove
              // @ts-ignore
              (data?.data?.allowed_payment_method_configs?.map(
                (item: AllowedPaymentMethodConfigPayload) => item?.payment_method_type || ''
              ) ||
                data?.data?.allowed_payment_methods ||
                []) as string[],
            customerServiceIsEnabled: !!data?.data?.customer_service_is_enabled,
            customerServiceNumber: data?.data?.customer_service_number,
            customerServiceUrlName: data?.data?.customer_service_url_name,
            customerServiceUrlLink: data?.data?.customer_service_url_link,
            customerServiceEmail: data?.data?.customer_service_email,
          }
        : undefined,
    [data]
  );

  return {
    organizationProperties: formattedData as OrganizationProperties,
    isError,
    isLoading,
    isSuccess,
  };
};

export default useOrganizationPropertiesQuery;
